import { useRouter } from "expo-router";
import type { ReactNode } from "react";
import { Platform, Pressable, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAuth } from "~/lib/auth";
import { backendConfigured } from "~/lib/constants";
import { PRO_TRIAL_DAYS } from "~/utils/pricing";
import { spacing } from "~/styles/tokens";
import { useTheme } from "~/styles/theme";
import { Text } from "./ui";

/** True once RevenueCat has answered (or failed), so Pro isn't guessed while loading. */
export function useProSettled() {
  const { user, purchases } = useAuth();
  if (!user) return true;
  return purchases.customerInfo !== null || purchases.error !== null;
}

/**
 * Mobile only: starter, recipes and timers sync only on Pro (the free trial
 * counts). Until then they live on this device and are never uploaded, and this
 * banner says so on every screen. The web app always requires Pro, so it never
 * shows this.
 */
function useLocalOnly() {
  const { ready, hasPro } = useAuth();
  const settled = useProSettled();
  return (
    Platform.OS !== "web" && backendConfigured && ready && settled && !hasPro
  );
}

function LocalDataBanner() {
  const { colors } = useTheme();
  const router = useRouter();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  return (
    <View
      style={{
        backgroundColor: colors.warningSurface,
        borderBottomWidth: 1,
        borderBottomColor: colors.warning,
        paddingHorizontal: spacing.md,
        paddingTop: insets.top + spacing.xs,
        paddingBottom: spacing.xs,
        gap: spacing.xs,
      }}
    >
      <Text variant="caption" color={colors.foreground}>
        Your starter, recipes and timers are only saved on this device. Start
        your {PRO_TRIAL_DAYS}-day free trial to back them up and sync across
        devices.
      </Text>
      <Pressable
        accessibilityRole="link"
        accessibilityLabel="Start your free trial"
        onPress={() => router.push(user ? "/subscription" : "/auth")}
      >
        <Text variant="label" color={colors.warning}>
          {user ? "Start free trial →" : "Create account & start free trial →"}
        </Text>
      </Pressable>
    </View>
  );
}

export function LocalDataFrame({ children }: { children: ReactNode }) {
  const localOnly = useLocalOnly();
  if (!localOnly) return <>{children}</>;
  return (
    <View style={{ flex: 1 }}>
      <LocalDataBanner />
      <View style={{ flex: 1 }}>{children}</View>
    </View>
  );
}
